import { useMemo, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { clsx } from 'clsx'
import { useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks'
import { pathURL } from '@/components/constants/urls'
import textsData from '@/textData/AppSideBarTexts.json'
import rentmoteIcon from '@/assets/rentmote-icon.svg'
import homeIcon from '@/assets/sidebarIcons/home-icon.svg'
import calculatorIcon from '@/assets/sidebarIcons/calculator-icon.svg'
import chartIcon from '@/assets/sidebarIcons/chart-icon.svg'
//import folderIcon from '@/assets/sidebarIcons/folder-icon.svg'
import gearIcon from '@/assets/sidebarIcons/gear-icon.svg'
import idCardIcon from '@/assets/sidebarIcons/idcard-icon.svg'
import notesIcon from '@/assets/sidebarIcons/notes-icon.svg'
import notificationIcon from '@/assets/sidebarIcons/notification-icon.svg'
import optionsIcon from '@/assets/sidebarIcons/options-icon.svg'
import paperIcon from '@/assets/sidebarIcons/paper-icon.svg'
import resumeIcon from '@/assets/sidebarIcons/resume-icon.svg'
import toolsIcon from '@/assets/sidebarIcons/tools-icon.svg'

import styles from './AppSideBar.module.css'

export const AppSideBar = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [isSelected, setIsSelected] = useState(-1);
  const [shakingIndex, setShakingIndex] = useState<number | null>(null);

  const { addMenuModalOpen } = useOpeningElementsControllerContext();

  const navigate = useNavigate();
  const location = useLocation();

  const buttons = useMemo(() => (
    [
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.HOME}`),
        icon: homeIcon,
        label: texts.home,
        matchers: [
          `/${pathURL.USER}`,
          `/${pathURL.USER}/`,
          `/${pathURL.USER}/${pathURL.HOME}`,
          `/${pathURL.USER}/${pathURL.HOME}/`,
        ],
        exactMatchOnly: true,
        isAvailable: true,
      },
      {
        onClickHandle: addMenuModalOpen,
        icon: optionsIcon,
        label: texts.create,
        matchers: [],
        isAvailable: true,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.PROPERTY}`),
        icon: notesIcon,
        label: texts.estate,
        matchers: [`/${pathURL.USER}/${pathURL.PROPERTY}`],
        isAvailable: true,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.CONTRACT}`),
        icon: paperIcon,
        label: texts.leasing,
        matchers: [`/${pathURL.USER}/${pathURL.CONTRACT}`],
        isAvailable: true,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.INCIDENTS}`),
        icon: toolsIcon,
        label: texts.incidents,
        matchers: [`/${pathURL.USER}/${pathURL.INCIDENTS}`],
        isAvailable: true,
      },
      {
        onClickHandle: () => console.log('presionado'),
        icon: calculatorIcon,
        label: texts.accounting,
        matchers: [],
        isAvailable: false,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.REPORTS}`),
        icon: chartIcon,
        label: texts.reports,
        matchers: [
          `/${pathURL.USER}/${pathURL.REPORTS}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.ALERT_NOTICES}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.RENT_CHARGES}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.EXPENSES}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.CONTRACTS_ROADMAP}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.DEPOSITS}`,
          `/${pathURL.USER}/${pathURL.REPORTS}/${pathURL.MANAGER_RATINGS}`,
        ],
        isAvailable: true,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.CONTACTS}`),
        icon: idCardIcon,
        label: texts.contacts,
        matchers: [`/${pathURL.USER}/${pathURL.CONTACTS}`],
        isAvailable: true,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.COMMUNICATIONS}`),
        icon: notificationIcon,
        label: texts.communications,
        matchers: [`/${pathURL.USER}/${pathURL.COMMUNICATIONS}`],
        isAvailable: true,
      },
      {
        onClickHandle: () => console.log('presionado'),
        icon: resumeIcon,
        label: texts.channels,
        matchers: [],
        isAvailable: false,
      },
      {
        onClickHandle: () => navigate(`/${pathURL.USER}/${pathURL.DETAIL}`),
        icon: gearIcon,
        label: texts.settings,
        matchers: [`/${pathURL.USER}/${pathURL.DETAIL}`],
        isAvailable: true,
      },
    ]
  ), [addMenuModalOpen, navigate, texts])

  useEffect(() => {
    const normalizedPath = location.pathname.toLowerCase();
    const foundIndex = buttons.findIndex((button) =>
      button.matchers?.some((matcher) =>
        button.exactMatchOnly
          ? normalizedPath === matcher.toLowerCase()
          : normalizedPath.startsWith(matcher.toLowerCase())
      )
    );

    setIsSelected(foundIndex);
  }, [buttons, location.pathname]);

  return (
    <aside
      className={clsx(
        'group fixed left-0 top-0 h-screen w-[65px] bg-background z-[100] transition-all duration-300 ease-in-out overflow-hidden hover:overflow-y-auto',
        'hover:w-[230px] hover:shadow-sidebar',
        styles.scrollbarNone
      )}
    >
      <div className="flex min-h-0 h-full flex-col px-2">
        <div className="flex w-full shrink-0 justify-center py-3 transition-all duration-300 translate-x-[6px] group-hover:translate-x-0 group-hover:w-full">
          <button
            type="button"
            className="border-none bg-transparent cursor-pointer px-1 py-1"
            onClick={() => {
              navigate(`/${pathURL.USER}`);
              setIsSelected(-1);
            }}
          >
            <img
              src={rentmoteIcon}
              alt=""
              className="block h-[64px] w-[64px] shrink-0 object-contain transition-all duration-300 group-hover:h-[58px] group-hover:w-[58px] hover:scale-110"
            />
          </button>
        </div>

        {/* Colapsado: íconos centrados en el rail; expandido: fila icono + texto */}
        <nav className="flex min-h-0 w-full flex-1 flex-col justify-evenly pb-2 pt-1">
          {buttons.map((button, index) => (
            <button
              key={index}
              type="button"
              className={clsx(
                'flex min-h-[48px] w-full max-w-full items-center rounded-base border-none p-0 text-inherit transition-all',
                'justify-center translate-x-[7px] group-hover:translate-x-0 group-hover:justify-start',
                'hover:bg-background2 focus:outline-none group-hover:text-left',
                index === isSelected ? 'bg-primary1/20 text-primary1' : 'bg-transparent hover:text-primary1',
                index === shakingIndex && styles.animateShake
              )}
              onClick={() => {
                if (!button.isAvailable) {
                  setShakingIndex(index);
                  setTimeout(() => setShakingIndex(null), 500);
                  return;
                }
                button.onClickHandle();
                setIsSelected(index);
              }}
              title={button.label}
            >
              <span
                className={clsx(
                  'flex size-11 shrink-0 items-center justify-center transition-all duration-200',
                  'group-hover:ml-0'
                )}
              >
                <img
                  src={button.icon}
                  width={24}
                  height={24}
                  alt=""
                  className={clsx(
                    'block h-6 w-6 object-contain transition-all duration-200',
                    index === isSelected ? 'scale-110' : 'opacity-80 group-hover:opacity-100'
                  )}
                />
              </span>
              <span
                className={clsx(
                  'min-w-0 shrink overflow-hidden whitespace-nowrap pl-0 text-left text-sm font-medium opacity-0 transition-all duration-300 ease-in-out',
                  'w-0 group-hover:w-[145px] group-hover:pl-1 group-hover:opacity-100',
                  index === shakingIndex && styles.animateShake
                )}
              >
                {button.label}
              </span>
            </button>
          ))}
        </nav>
      </div>
    </aside>
  );
};
